// ComplianceDisclosures.js — InvestorQ Private Market
// Centralized disclosure and disclaimer layer — v1.0
// OffBeet Media & Tech Inc. · investorq.io
//
// USAGE:
//   Import the component or text constant you need at each surface.
//   Never inline ad-hoc disclaimers — always pull from this file so
//   legal updates propagate platform-wide from one place.
//
// IMPORTANT: These disclosures were drafted with the assistance of AI
// and are intended as a starting framework. They must be reviewed and
// approved by a licensed securities attorney before commercial launch.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// DISCLOSURE TEXT CONSTANTS
// All raw text strings — use these in meta tags, emails, PDFs, and anywhere
// React components are not available.
// ─────────────────────────────────────────────────────────────────────────────

export const DISCLOSURES = {

  // ── Platform-level (shown site-wide in footer and on landing) ──────────────
  PLATFORM_GENERAL: `InvestorQ is a private market intelligence and discovery platform operated by OffBeet Media & Tech Inc. InvestorQ is not a registered broker-dealer, investment adviser, funding portal, or transfer agent. Nothing on this platform constitutes an offer to sell, a solicitation of an offer to buy, or a recommendation with respect to any security. All valuations displayed are editorial estimates based on publicly available information unless explicitly labeled as "Tier B Certified," in which case they are based on organization-provided materials reviewed under a Mutual Non-Disclosure Agreement. Past performance is not indicative of future results. Private market assets are illiquid and speculative. You may lose your entire investment.`,

  // ── Tier A specific ────────────────────────────────────────────────────────
  TIER_A_EDITORIAL: `TIER A — EDITORIAL ESTIMATE. This valuation is generated by InvestorQ's proprietary model using publicly available data only. It has not been reviewed, verified, or approved by the organization listed. It does not represent a formal appraisal, fairness opinion, or investment recommendation. This asset has not consented to listing on InvestorQ and has no affiliation with InvestorQ or OffBeet Media & Tech Inc.`,

  // ── Shadow tokens ──────────────────────────────────────────────────────────
  SHADOW_TOKEN: `SHADOW TOKEN — SIMULATION ONLY. Shadow tokens are synthetic tracking instruments that simulate the price behavior of private sports assets for educational and analytical purposes. They are not securities, not redeemable for any asset or currency, not tradeable on any exchange, and have no monetary value. They are not registered under the Securities Act of 1933 or any state securities law. Shadow token pricing is derived from InvestorQ's editorial valuation model and does not represent a market price or a binding transaction.`,

  // ── Tier B certified ──────────────────────────────────────────────────────
  TIER_B_CERTIFIED: `TIER B — CERTIFIED INTELLIGENCE. This organization has executed a Mutual Non-Disclosure Agreement with InvestorQ and has submitted financial and organizational materials for review. InvestorQ's certification reflects that submitted materials have been reviewed under our internal framework, not that they have been independently audited, verified by a third-party CPA, or approved by any regulatory authority. Certification is not a recommendation to invest. Certified status expires annually and is subject to revocation.`,

  // ── Deal room ─────────────────────────────────────────────────────────────
  DEAL_ROOM: `DEAL ROOM — CONFIDENTIAL. Documents accessible in this Deal Room are provided by the listed organization under a Mutual Non-Disclosure Agreement between InvestorQ and the accessing investor. By accessing these materials you agree to: (1) maintain strict confidentiality; (2) use the materials solely to evaluate a potential investment in the listed organization; (3) not reproduce, distribute, or disclose any materials to third parties without written consent. These materials have not been filed with or approved by the SEC or any state securities regulator. They may contain forward-looking statements that are subject to risk and uncertainty. InvestorQ makes no representation as to the accuracy, completeness, or fairness of any information contained herein.`,

  // ── Live raises (Tier C) ──────────────────────────────────────────────────
  TIER_C_RAISE: `LIVE INVESTMENT OFFERING. This offering is conducted pursuant to Regulation Crowdfunding (17 CFR § 227) or Regulation A (17 CFR § 230.251 et seq.) under the Securities Act of 1933, as amended. The offering is being made exclusively through a registered funding portal or licensed broker-dealer partner identified on the offering page. InvestorQ is not a party to the offering, does not handle investor funds or securities, and does not receive compensation in connection with investment commitments. Investing involves risk, including loss of principal. Investments are illiquid and may not be resold unless registered or exempt. Non-accredited investors are subject to investment limits under SEC rules. Review all offering materials including the Form C or Offering Circular before investing.`,

  // ── Investor interest registration ────────────────────────────────────────
  INVESTOR_INTEREST: `INDICATION OF INTEREST — NOT A COMMITMENT. Submitting an indication of interest does not constitute a binding investment commitment, a reservation of securities, or an obligation on the part of InvestorQ or the listed organization. This feature is provided for informational purposes only to allow organizations to gauge investor demand prior to a potential offering. InvestorQ does not collect investor funds, hold securities, or act as an intermediary in any securities transaction.`,

  // ── Accredited investor ───────────────────────────────────────────────────
  ACCREDITED_INVESTOR: `ACCREDITED INVESTOR SELF-CERTIFICATION. Certain features of this platform, including access to specific Deal Room materials and Regulation D offerings, are available only to "accredited investors" as defined in Rule 501(a) of Regulation D under the Securities Act of 1933. By confirming accredited investor status, you represent and warrant that you qualify under at least one of the enumerated criteria (e.g., annual income exceeding $200,000 individually or $300,000 jointly for the past two years with a reasonable expectation of the same in the current year; net worth exceeding $1,000,000 excluding primary residence; or other qualifying criteria). InvestorQ does not independently verify accredited investor status. Misrepresentation of accredited investor status may constitute fraud under applicable securities laws.`,

  // ── Valuation methodology ────────────────────────────────────────────────
  VALUATION_METHODOLOGY: `VALUATION METHODOLOGY DISCLOSURE. InvestorQ's 4-method valuation framework applies the following approaches: (1) Revenue Multiple — comparable transaction multiples from public and disclosed private transactions; (2) EBITDA Multiple — earnings-based approach calibrated to comparable sports and entertainment assets; (3) Discounted Cash Flow (DCF) — projected free cash flows discounted at a risk-adjusted rate; (4) Brand/Fan Equity — proprietary scoring model incorporating media reach, social engagement, and brand licensing potential. For Tier A assets, all inputs are editorial estimates derived from publicly available information. For Tier B assets, verified financials from the organization are incorporated where available. All valuations represent a range and point estimate at a specific date and are subject to material change. Valuations are not appraisals and should not be relied upon as the sole basis for any investment decision.`,

  // ── Not investment advice ─────────────────────────────────────────────────
  NOT_INVESTMENT_ADVICE: `NOT INVESTMENT ADVICE. Nothing on this platform constitutes investment advice, financial advice, legal advice, or tax advice. InvestorQ is not a registered investment adviser under the Investment Advisers Act of 1940. Any information provided is for general informational and educational purposes only. You should consult with a qualified financial adviser, attorney, and tax professional before making any investment decision.`,

  // ── Forward-looking statements ────────────────────────────────────────────
  FORWARD_LOOKING: `FORWARD-LOOKING STATEMENTS. Certain information on this platform may include projections, forecasts, and other forward-looking statements. These statements involve known and unknown risks, uncertainties, and other factors that may cause actual results to differ materially from those expressed or implied. InvestorQ and the listed organizations undertake no obligation to update forward-looking statements to reflect new information, future events, or changed circumstances.`,

  // ── Email footer (plain text for nodemailer) ──────────────────────────────
  EMAIL_FOOTER_PLAIN: `InvestorQ Private Market · OffBeet Media & Tech Inc. · investorq.io
This communication is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any security. InvestorQ is not a registered broker-dealer or investment adviser. Investing in private market assets involves significant risk including loss of principal. Assets are illiquid and may not be resold. Past performance is not indicative of future results.`,

};

// ─────────────────────────────────────────────────────────────────────────────
// REACT DISCLOSURE COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

// ── Site-wide footer disclaimer bar ─────────────────────────────────────────
export function FooterDisclosure({ compact = false }) {
  return (
    <div style={{
      borderTop: '1px solid rgba(255,255,255,0.06)',
      padding: compact ? '12px 16px' : '20px 32px',
      background: 'rgba(0,0,0,0.3)',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          fontSize: 10, color: '#334155', lineHeight: 1.7,
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          <span style={{ color: '#475569', fontWeight: 700 }}>InvestorQ</span> is operated by OffBeet Media &amp; Tech Inc.
          and is <strong style={{ color: '#475569' }}>not a registered broker-dealer, investment adviser, or funding portal.</strong>{' '}
          Nothing on this platform constitutes an offer to sell or a solicitation to buy any security.
          Tier A valuations are editorial estimates using public data only.
          Tier B valuations incorporate organization-verified materials reviewed under NDA — they are not independent audits.
          Shadow tokens are simulations with no monetary value and are not securities.
          Live raises (Tier C) are conducted exclusively through our registered broker-dealer partners; InvestorQ does not handle investor funds.
          All private market investments are <strong style={{ color: '#475569' }}>illiquid and speculative</strong> — you may lose your entire investment.
          Past performance does not predict future results.
          {' '}<a href="/legal/terms" style={{ color: '#7c6fff', textDecoration: 'none' }}>Terms of Service</a>
          {' '}·{' '}
          <a href="/legal/privacy" style={{ color: '#7c6fff', textDecoration: 'none' }}>Privacy Policy</a>
          {' '}·{' '}
          <a href="/legal/disclosures" style={{ color: '#7c6fff', textDecoration: 'none' }}>Full Disclosures</a>
        </div>
      </div>
    </div>
  );
}

// ── Inline tier-specific disclosure banner ───────────────────────────────────
export function TierDisclosure({ tier, certified = false, compact = false }) {
  const configs = {
    A: {
      color: '#00d4aa', bg: 'rgba(0,212,170,0.05)', border: 'rgba(0,212,170,0.15)',
      icon: '◎',
      headline: 'Editorial estimate — Tier A',
      text: 'Valuation derived from public data only. Organization has not consented to or verified this listing. Not investment advice.',
    },
    B: {
      color: '#7c6fff', bg: 'rgba(124,111,255,0.05)', border: 'rgba(124,111,255,0.15)',
      icon: '◈',
      headline: certified ? 'Tier B Certified — organization-verified' : 'Tier B — Certification pending',
      text: certified
        ? 'Organization has executed NDA and submitted financial materials reviewed by InvestorQ. Not independently audited. Not investment advice.'
        : 'Certification in progress. Materials under review. Valuation is preliminary.',
    },
    C: {
      color: '#f5a623', bg: 'rgba(245,166,35,0.05)', border: 'rgba(245,166,35,0.15)',
      icon: '◆',
      headline: 'Live offering — Tier C',
      text: 'Offered under Reg CF/Reg A+ through our registered broker-dealer partner. InvestorQ does not handle funds. Review all offering materials before investing.',
    },
  };
  const cfg = configs[tier] || configs.A;

  return (
    <div style={{
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius: 8, padding: compact ? '6px 10px' : '10px 14px',
      display: 'flex', gap: 8, alignItems: 'flex-start',
    }}>
      <span style={{ fontSize: 12, color: cfg.color, flexShrink: 0, marginTop: 1 }}>{cfg.icon}</span>
      <div>
        <div style={{ fontSize: 10, fontWeight: 800, color: cfg.color,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
          {cfg.headline}
        </div>
        <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.5 }}>{cfg.text}</div>
      </div>
    </div>
  );
}

// ── Shadow token disclaimer ──────────────────────────────────────────────────
export function ShadowTokenDisclaimer({ compact = false }) {
  return (
    <div style={{
      background: 'rgba(124,111,255,0.04)', border: '1px solid rgba(124,111,255,0.12)',
      borderRadius: 8, padding: compact ? '6px 10px' : '10px 14px',
    }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#7c6fff',
        textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 3 }}>
        🔮 Shadow Token — Simulation Only
      </div>
      <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.5 }}>
        Not a security. Not redeemable. No monetary value. Educational simulation only.
        Pricing is editorial — not a market price. Not registered under any securities law.
      </div>
    </div>
  );
}

// ── Deal room access disclaimer (shown before NDA gate) ──────────────────────
export function DealRoomDisclaimer({ onAccept, accepted = false }) {
  const [checked, setChecked] = useState(false);

  if (accepted) return null;

  return (
    <div style={{
      background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(124,111,255,0.3)',
      borderRadius: 16, padding: '24px 28px', maxWidth: 560,
    }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#e2e8f0', marginBottom: 12 }}>
        🔒 Confidentiality Acknowledgment
      </div>
      <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.7, marginBottom: 16 }}>
        By accessing Deal Room materials you acknowledge and agree that:
      </div>
      <ul style={{ fontSize: 11, color: '#64748b', lineHeight: 2, paddingLeft: 16, marginBottom: 16 }}>
        <li>All materials are <strong style={{ color: '#94a3b8' }}>strictly confidential</strong> and covered by your executed NDA.</li>
        <li>Materials are provided solely to evaluate a potential investment and may not be shared.</li>
        <li>Materials have <strong style={{ color: '#94a3b8' }}>not been approved by the SEC</strong> or any regulatory authority.</li>
        <li>InvestorQ makes no representation as to accuracy or completeness.</li>
        <li>This access does not constitute investment advice or a recommendation to invest.</li>
      </ul>
      <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', marginBottom: 16 }}>
        <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
          style={{ accentColor: '#7c6fff', width: 14, height: 14 }} />
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          I have read and agree to the above. I confirm I have executed the required NDA.
        </span>
      </label>
      <button onClick={() => checked && onAccept()} disabled={!checked} style={{
        width: '100%', padding: '10px', borderRadius: 10, border: 'none', cursor: checked ? 'pointer' : 'not-allowed',
        background: checked ? 'linear-gradient(135deg, #7c6fff, #9b8fff)' : 'rgba(255,255,255,0.06)',
        color: checked ? '#fff' : '#475569', fontSize: 12, fontWeight: 800, letterSpacing: '0.06em',
        transition: 'all 0.2s',
      }}>
        Enter Deal Room →
      </button>
    </div>
  );
}

// ── Investor interest disclaimer ─────────────────────────────────────────────
export function InterestDisclaimer({ compact = false }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 8, padding: compact ? '6px 10px' : '10px 14px',
    }}>
      <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.5 }}>
        <strong style={{ color: '#64748b' }}>Indication of interest only.</strong>{' '}
        Not a binding commitment. InvestorQ does not collect funds or hold securities.
        Submitting interest does not guarantee allocation in any future offering.
      </div>
    </div>
  );
}

// ── Live raise disclaimer (Tier C) ───────────────────────────────────────────
export function RaiseDisclaimer({ platform = '', raiseType = 'Reg CF', compact = false }) {
  return (
    <div style={{
      background: 'rgba(245,166,35,0.04)', border: '1px solid rgba(245,166,35,0.15)',
      borderRadius: 8, padding: compact ? '6px 10px' : '10px 14px',
    }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#f5a623',
        textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 3 }}>
        ◆ {raiseType} Offering Disclosure
      </div>
      <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.6 }}>
        This offering is conducted through{platform ? ` ${platform},` : ''} a registered
        {raiseType === 'Reg A+' ? ' broker-dealer' : ' funding portal'} under SEC {raiseType} rules.
        InvestorQ does not handle investor funds or securities.
        Investments are <strong style={{ color: '#64748b' }}>illiquid, speculative, and subject to loss of principal.</strong>{' '}
        Non-accredited investors are subject to annual investment limits.
        Review all offering materials including the {raiseType === 'Reg A+' ? 'Offering Circular' : 'Form C'} before investing.
      </div>
    </div>
  );
}

// ── Accredited investor gate ─────────────────────────────────────────────────
export function AccreditedGate({ onConfirm, onDecline }) {
  const [selected, setSelected] = useState(null);

  const criteria = [
    'Annual income > $200K (individual) or $300K (joint) for past 2 years with expectation of same',
    'Net worth > $1,000,000 excluding primary residence (individual or joint)',
    'Currently licensed Series 7, 65, or 82 securities professional in good standing',
    'Knowledgeable employee of a qualifying private fund',
    'Entity with total assets > $5M not formed solely to invest',
  ];

  return (
    <div style={{
      background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(124,111,255,0.3)',
      borderRadius: 16, padding: '28px 32px', maxWidth: 520,
    }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#e2e8f0', marginBottom: 8 }}>
        Accredited Investor Verification
      </div>
      <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.6, marginBottom: 20 }}>
        This material is available only to <strong style={{ color: '#94a3b8' }}>accredited investors</strong> as
        defined in Rule 501(a) of SEC Regulation D. Please confirm you qualify under at least one criterion below.
        InvestorQ does not independently verify this status — misrepresentation may constitute securities fraud.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {criteria.map((c, i) => (
          <label key={i} style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
            padding: '8px 10px', borderRadius: 8,
            background: selected === i ? 'rgba(124,111,255,0.08)' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${selected === i ? 'rgba(124,111,255,0.3)' : 'rgba(255,255,255,0.06)'}`,
            transition: 'all 0.15s',
          }}>
            <input type="radio" name="accredited" value={i} checked={selected === i}
              onChange={() => setSelected(i)} style={{ accentColor: '#7c6fff', marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: selected === i ? '#94a3b8' : '#64748b', lineHeight: 1.5 }}>{c}</span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => selected !== null && onConfirm()} disabled={selected === null} style={{
          flex: 1, padding: '10px', borderRadius: 10, border: 'none',
          cursor: selected !== null ? 'pointer' : 'not-allowed',
          background: selected !== null ? 'linear-gradient(135deg, #7c6fff, #9b8fff)' : 'rgba(255,255,255,0.06)',
          color: selected !== null ? '#fff' : '#475569',
          fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', transition: 'all 0.2s',
        }}>
          I qualify — Continue →
        </button>
        <button onClick={onDecline} style={{
          padding: '10px 16px', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.08)', background: 'transparent',
          color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer',
        }}>
          I do not qualify
        </button>
      </div>
      <div style={{ marginTop: 12, fontSize: 9, color: '#334155', lineHeight: 1.5 }}>
        By confirming, you represent under penalty of law that you meet the selected criterion.
        This confirmation is logged and associated with your session.
      </div>
    </div>
  );
}

// ── Valuation methodology tooltip ────────────────────────────────────────────
export function ValuationMethodologyBadge({ certified = false }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
        fontSize: 9, fontWeight: 700, color: '#475569', letterSpacing: '0.08em',
      }}>
        {certified ? '◈ AICPA-Aligned · Verified' : '◎ Editorial Estimate'} ⓘ
      </button>
      {open && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
          background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 10, padding: '14px 16px', width: 300, zIndex: 100,
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', marginBottom: 8 }}>
            4-Method Valuation Framework
          </div>
          {[
            ['Revenue Multiple', certified ? 'Verified revenue from org financials' : 'Editorial estimate from public filings'],
            ['EBITDA Multiple', certified ? 'Verified EBITDA from org financials' : 'Editorial estimate'],
            ['DCF Model', 'Projected FCF discounted at risk-adjusted rate'],
            ['Brand / Fan Equity', 'Proprietary score: media reach + social + licensing'],
          ].map(([method, note]) => (
            <div key={method} style={{ display: 'flex', justifyContent: 'space-between',
              padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
              fontSize: 10, color: '#64748b' }}>
              <span style={{ fontWeight: 700, color: '#94a3b8' }}>{method}</span>
              <span style={{ textAlign: 'right', maxWidth: 160 }}>{note}</span>
            </div>
          ))}
          <div style={{ marginTop: 8, fontSize: 9, color: '#334155', lineHeight: 1.5 }}>
            {certified
              ? 'Valuation incorporates org-provided materials reviewed under NDA. Not an independent audit. AICPA-aligned methodology.'
              : 'Editorial estimate using public data only. Not investment advice.'}
          </div>
          <button onClick={() => setOpen(false)} style={{
            marginTop: 8, width: '100%', padding: '5px', background: 'transparent',
            border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6,
            color: '#475569', fontSize: 10, cursor: 'pointer',
          }}>Close</button>
        </div>
      )}
    </div>
  );
}

// ── One-line compliance notice (for tile cards) ──────────────────────────────
export function TileComplianceNote({ tier, certified }) {
  if (tier === 'C') return (
    <div style={{ fontSize: 9, color: '#475569', marginTop: 4 }}>
      ◆ Raise cleared by registered broker-dealer · InvestorQ is not the issuer
    </div>
  );
  if (tier === 'B' && certified) return (
    <div style={{ fontSize: 9, color: '#475569', marginTop: 4 }}>
      ◈ Org-verified under NDA · Not independently audited · Not investment advice
    </div>
  );
  return (
    <div style={{ fontSize: 9, color: '#475569', marginTop: 4 }}>
      ◎ Editorial estimate · Public data only · Organization not affiliated
    </div>
  );
}

// ── Pre-investment risk warning (shown before any "Invest" CTA) ──────────────
export function PreInvestmentWarning({ onContinue, platform = '' }) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <div style={{
      background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.2)',
      borderRadius: 14, padding: '20px 22px',
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#f87171', marginBottom: 12 }}>
        ⚠ Investment Risk Disclosure
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {[
          'This is a high-risk, speculative investment in a private company.',
          'You may lose your entire investment. Private assets are illiquid.',
          'There is no secondary market — you cannot easily sell your investment.',
          'Returns are not guaranteed. Past performance does not predict future results.',
          `You are being redirected to ${platform || 'our broker-dealer partner'} to complete your investment. InvestorQ will not handle your funds.`,
        ].map((risk, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, color: '#94a3b8', alignItems: 'flex-start' }}>
            <span style={{ color: '#f87171', flexShrink: 0, fontWeight: 800 }}>•</span>
            {risk}
          </div>
        ))}
      </div>
      <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', marginBottom: 14 }}>
        <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)}
          style={{ accentColor: '#f5a623', width: 14, height: 14 }} />
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          I understand the risks. I am prepared to lose my entire investment.
        </span>
      </label>
      <button onClick={() => acknowledged && onContinue()} disabled={!acknowledged} style={{
        width: '100%', padding: '11px', borderRadius: 10, border: 'none',
        cursor: acknowledged ? 'pointer' : 'not-allowed',
        background: acknowledged ? 'linear-gradient(135deg, #f5a623, #ffc75f)' : 'rgba(255,255,255,0.06)',
        color: acknowledged ? '#000' : '#475569',
        fontSize: 12, fontWeight: 900, letterSpacing: '0.06em', transition: 'all 0.2s',
      }}>
        {acknowledged ? `Proceed to ${platform || 'Investment Platform'} →` : 'Please acknowledge the risks above'}
      </button>
    </div>
  );
}
